import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Coffee, Copy, Save, CalendarClock, UserCircle2 } from "lucide-react";
import { toast } from "sonner";

const DAYS_SQ = ["E Hënë", "E Martë", "E Mërkurë", "E Enjte", "E Premte", "E Shtunë", "E Diel"];

const emptyDay = () => ({
  is_working: false,
  start_time: "",
  end_time: "",
  break_start: "",
  break_end: "",
});

const defaultDays = () => {
  // Mon-Fri 09:00-17:00 lunch 13:00-14:00; Sat-Sun off
  return Array.from({ length: 7 }, (_, i) => {
    if (i <= 4) {
      return {
        is_working: true,
        start_time: "09:00",
        end_time: "17:00",
        break_start: "13:00",
        break_end: "14:00",
      };
    }
    return emptyDay();
  });
};

const normalizeDays = (raw) => {
  if (!Array.isArray(raw) || raw.length !== 7) return defaultDays();
  return raw.map((d) => ({
    is_working: !!d?.is_working,
    start_time: d?.start_time || "",
    end_time: d?.end_time || "",
    break_start: d?.break_start || "",
    break_end: d?.break_end || "",
  }));
};

export default function DoctorSchedules() {
  const [doctors, setDoctors] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [days, setDays] = useState(defaultDays());
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/doctors");
        setDoctors(data || []);
        if ((data || []).length && !selectedId) {
          setSelectedId(data[0].id);
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gabim në ngarkimin e doktorëve");
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/doctors/${selectedId}/schedule`);
        setDays(normalizeDays(data?.days));
        setExists(!!data?.exists);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gabim në ngarkimin e orarit");
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedId]);

  const selectedDoctor = useMemo(
    () => doctors.find((d) => d.id === selectedId),
    [doctors, selectedId]
  );

  const updateDay = (idx, patch) => {
    setDays((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  };

  const copyMondayToWeekdays = () => {
    const m = days[0];
    setDays((prev) =>
      prev.map((d, i) => (i >= 1 && i <= 4 ? { ...m } : d))
    );
    toast.success("U kopjua orari i së Hënës te ditët e javës");
  };

  const clearAll = () => {
    setDays(Array.from({ length: 7 }, emptyDay));
    toast.message("Të gjitha ditët u shënuan si pushim");
  };

  const applyTemplate = (template) => {
    if (template === "mf_9_17") {
      setDays(defaultDays());
    } else if (template === "mf_8_16") {
      setDays(
        Array.from({ length: 7 }, (_, i) =>
          i <= 4
            ? { is_working: true, start_time: "08:00", end_time: "16:00", break_start: "12:00", break_end: "13:00" }
            : emptyDay()
        )
      );
    } else if (template === "ms_9_13") {
      setDays(
        Array.from({ length: 7 }, (_, i) =>
          i <= 5
            ? { is_working: true, start_time: "09:00", end_time: "13:00", break_start: "", break_end: "" }
            : emptyDay()
        )
      );
    }
    toast.success("Shabllon u aplikua");
  };

  const save = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      // sanitize: clear time fields when not working
      const payload = {
        days: days.map((d) => ({
          is_working: !!d.is_working,
          start_time: d.is_working ? d.start_time || null : null,
          end_time: d.is_working ? d.end_time || null : null,
          break_start: d.is_working ? d.break_start || null : null,
          break_end: d.is_working ? d.break_end || null : null,
        })),
      };
      await api.put(`/doctors/${selectedId}/schedule`, payload);
      setExists(true);
      toast.success("Orari u ruajt me sukses");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gabim në ruajtjen e orarit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="doctor-schedules-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#1A1A1A] flex items-center gap-3">
            <CalendarClock className="text-[#2C4C3B]" size={28} />
            Orari i Doktorëve
          </h1>
          <p className="text-sm text-[#5C5C5C] mt-1">
            Cakto orarin e punës javor për çdo doktor. Takimet e reja do të respektojnë automatikisht këto orare.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Doctors list */}
        <Card data-testid="doctors-list-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Doktorët</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {doctors.length === 0 ? (
              <p className="text-sm text-[#5C5C5C]">Nuk ka doktorë në sistem.</p>
            ) : (
              doctors.map((d) => (
                <button
                  key={d.id}
                  data-testid={`doctor-item-${d.id}`}
                  onClick={() => setSelectedId(d.id)}
                  className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition ${
                    selectedId === d.id
                      ? "bg-[#2C4C3B] text-white"
                      : "hover:bg-[#EAE4D9] text-[#1A1A1A]"
                  }`}
                >
                  <UserCircle2 size={18} />
                  <span className="truncate">{d.name}</span>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        {/* Schedule editor */}
        <Card data-testid="schedule-editor-card">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base">
                  {selectedDoctor ? selectedDoctor.name : "Zgjidh një doktor"}
                </CardTitle>
                {selectedDoctor && (
                  <p className="text-xs text-[#5C5C5C] mt-1">
                    {exists ? "Orari ekzistues" : "Orar i ri (paraset i propozuar)"}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="template-mf-9-17"
                  onClick={() => applyTemplate("mf_9_17")}
                >
                  Hënë–Premte 9–17
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="template-mf-8-16"
                  onClick={() => applyTemplate("mf_8_16")}
                >
                  Hënë–Premte 8–16
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="template-ms-9-13"
                  onClick={() => applyTemplate("ms_9_13")}
                >
                  Hënë–Shtunë 9–13
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="copy-monday-button"
                  onClick={copyMondayToWeekdays}
                >
                  <Copy size={14} className="mr-1" /> Kopjo të Hënën
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="clear-all-button"
                  onClick={clearAll}
                >
                  Pastro të gjitha
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-[#5C5C5C]">Po ngarkohet…</p>
            ) : !selectedId ? (
              <p className="text-sm text-[#5C5C5C]">Zgjidh një doktor nga lista.</p>
            ) : (
              <div className="space-y-3">
                {days.map((d, idx) => (
                  <div
                    key={idx}
                    data-testid={`day-row-${idx}`}
                    className={`rounded-lg border ${
                      d.is_working ? "border-[#C9C2B2] bg-white" : "border-dashed border-[#D9D6CE] bg-[#FAF8F2]"
                    } p-4`}
                  >
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-3 w-40">
                        <Switch
                          checked={d.is_working}
                          onCheckedChange={(v) => updateDay(idx, { is_working: v })}
                          data-testid={`working-toggle-${idx}`}
                        />
                        <span className="font-medium text-[#1A1A1A]">{DAYS_SQ[idx]}</span>
                      </div>

                      {d.is_working ? (
                        <>
                          <div className="flex items-center gap-2">
                            <Clock size={16} className="text-[#2C4C3B]" />
                            <Label className="text-xs text-[#5C5C5C]">Fillim</Label>
                            <Input
                              type="time"
                              value={d.start_time}
                              onChange={(e) => updateDay(idx, { start_time: e.target.value })}
                              className="w-28"
                              data-testid={`start-time-${idx}`}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-[#5C5C5C]">Mbarim</Label>
                            <Input
                              type="time"
                              value={d.end_time}
                              onChange={(e) => updateDay(idx, { end_time: e.target.value })}
                              className="w-28"
                              data-testid={`end-time-${idx}`}
                            />
                          </div>
                          <div className="flex items-center gap-2 ml-auto">
                            <Coffee size={16} className="text-[#9E7B3C]" />
                            <Label className="text-xs text-[#5C5C5C]">Drekë</Label>
                            <Input
                              type="time"
                              value={d.break_start}
                              onChange={(e) => updateDay(idx, { break_start: e.target.value })}
                              className="w-24"
                              placeholder="--:--"
                              data-testid={`break-start-${idx}`}
                            />
                            <span className="text-[#5C5C5C]">–</span>
                            <Input
                              type="time"
                              value={d.break_end}
                              onChange={(e) => updateDay(idx, { break_end: e.target.value })}
                              className="w-24"
                              placeholder="--:--"
                              data-testid={`break-end-${idx}`}
                            />
                            {(d.break_start || d.break_end) && (
                              <button
                                type="button"
                                onClick={() => updateDay(idx, { break_start: "", break_end: "" })}
                                className="text-xs text-[#9E3C3C] underline ml-1"
                                data-testid={`clear-break-${idx}`}
                              >
                                Hiq
                              </button>
                            )}
                          </div>
                        </>
                      ) : (
                        <span className="text-sm text-[#5C5C5C] italic">Pushim</span>
                      )}
                    </div>
                  </div>
                ))}

                <div className="flex justify-end pt-2">
                  <Button
                    onClick={save}
                    disabled={saving}
                    className="bg-[#2C4C3B] hover:bg-[#1F3A2B] text-white"
                    data-testid="save-schedule-button"
                  >
                    <Save size={16} className="mr-2" />
                    {saving ? "Po ruhet…" : "Ruaj Orarin"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="text-xs text-[#5C5C5C] bg-[#FAF8F2] border border-[#E2DFD8] rounded-lg p-4">
        <strong>Shënim:</strong> Validimi i orarit zbatohet vetëm për takimet e reja ose ato që modifikohen pas ruajtjes.
        Takimet ekzistuese nuk ndikohen.
      </div>
    </div>
  );
}
